import type { ColorParam } from '@paint-ai/shared';
import { CheckCircle2, FileCheck, Paintbrush, Printer, ShieldCheck, X } from 'lucide-react';
import { Button, Modal } from '@/components/ui';
import type { ColorScheme, ColorSlot } from '../state/studio-reducer';

const SURFACES: { slot: ColorSlot; title: string; product: (color: ColorParam) => string }[] = [
  { slot: 'wall', title: 'WALL (Primary)', product: (c) => `Asian Paints: Apex Ultima ${c.name}` },
  { slot: 'border', title: 'BORDER (Trim)', product: () => 'Asian Paints: Apex Trim Finish' },
  { slot: 'highlight', title: 'HIGHLIGHT (Accent)', product: () => 'Asian Paints / WoodTech Enamel' },
];

const GUIDELINES: [title: string, text: string][] = [
  ['Surface Normalization', 'Clean raw brickwork, cure plaster for 28 days, and scrape loose mortar debris.'],
  ['Primer Coat', 'Apply 1 coat of Exterior Wall Primer diluted 1:1 with clean water. Allow 4-6 hours drying.'],
  ['Waterproofing', 'Apply 1 elastomeric waterproofing coat prior to topcoat in high rainfall zones.'],
  ['Topcoat Finish', '2 coats of Premium Exterior Emulsion in Semi-Gloss / Matte finish to match assigned HEX values.'],
];

function SurfaceSpec({ title, color, product }: { title: string; color: ColorParam; product: string }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-800">{title}</span>
        <span className="text-[10px] font-mono font-bold text-blue-600">{color.type}</span>
      </div>
      <div
        className="w-full h-12 rounded-lg border border-slate-300 shadow-inner flex items-end p-2"
        style={{ backgroundColor: color.hex }}
      >
        <span className="text-xs font-mono font-bold bg-white/90 px-2 py-0.5 rounded text-slate-900 shadow-xs">
          {color.hex}
        </span>
      </div>
      <div className="text-xs space-y-1 pt-1">
        <p className="font-bold text-slate-800">{color.name}</p>
        {color.code && <p className="text-[11px] text-blue-700 font-bold font-mono">Shade Code: {color.code}</p>}
        <p className="text-[11px] text-slate-500 font-mono">RGB: ({color.rgb})</p>
        <p className="text-[11px] text-blue-700 font-bold">
          {product} {color.code ? `(${color.code})` : ''}
        </p>
      </div>
    </div>
  );
}

interface PaintSpecReportModalProps {
  open: boolean;
  onClose: () => void;
  colors: ColorScheme;
}

/** Printable paint specification for the selected colour scheme. */
export function PaintSpecReportModal({ open, onClose, colors }: PaintSpecReportModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      labelledBy="paint-spec-title"
      className="bg-white border border-slate-200 rounded-2xl w-full max-w-3xl shadow-2xl text-slate-800 my-8 overflow-hidden"
    >
      <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-xl bg-blue-50 text-blue-600 border border-blue-100">
            <FileCheck className="w-6 h-6" />
          </div>
          <div>
            <h2 id="paint-spec-title" className="text-lg font-bold text-slate-900">
              Exterior Paint Specifications Report
            </h2>
            <p className="text-xs text-slate-500">
              Architectural color matching & technical coating guidelines for Indian climate
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close report"
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
          id="close-paint-spec-modal"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto custom-scrollbar" id="printable-spec-sheet">
        <div className="bg-slate-900 text-white rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-md">
          <div>
            <span className="text-[10px] uppercase font-mono tracking-widest text-blue-400 font-bold block">
              Official Spec Card
            </span>
            <h3 className="text-base font-bold text-white">Indian Exterior Architectural Finish Specification</h3>
            <p className="text-xs text-slate-300">Generated for high UV resistance & monsoon waterproofing</p>
          </div>
          <div className="text-right text-xs font-mono text-slate-400">
            Date: {new Date().toLocaleDateString('en-IN', { dateStyle: 'medium' })}
          </div>
        </div>

        <section className="space-y-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-blue-600 flex items-center gap-1.5">
            <Paintbrush className="w-4 h-4" /> Selected Color Palette Specification
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {SURFACES.map(({ slot, title, product }) => (
              <SurfaceSpec key={slot} title={title} color={colors[slot]} product={product(colors[slot])} />
            ))}
          </div>
        </section>

        <section className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-blue-600 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4" /> Technical Application Guidelines
          </h4>
          <ul className="text-xs space-y-2 text-slate-700">
            {GUIDELINES.map(([title, text]) => (
              <li key={title} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>{title}:</strong> {text}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <Button
          variant="secondary"
          onClick={onClose}
          className="bg-slate-200 hover:bg-slate-300 text-slate-800 border-0"
        >
          Close Report
        </Button>
        <Button
          onClick={() => window.print()}
          className="gap-2 shadow-md shadow-blue-600/20"
          id="print-spec-report-btn"
        >
          <Printer className="w-4 h-4" /> Print / Save PDF
        </Button>
      </div>
    </Modal>
  );
}
